import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { Badge } from '../../components/common/Badge';
import { 
  LayoutDashboard, Bookmark, Calendar, Bell, 
  MessageSquare, CreditCard, Search, 
  AlertCircle, ChevronRight, User as UserIcon, Send, Trash2, Gift, Copy, ShoppingCart, Home, Check, Banknote, X
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';

// Simple Toast Component
const Toast: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="fixed top-24 right-4 z-50 bg-white border border-gray-200 shadow-xl rounded-lg p-4 flex items-start gap-3 max-w-sm animate-fadeIn">
    <div className="text-green-500 mt-0.5">
      <MessageSquare className="w-5 h-5" />
    </div>
    <div className="flex-1">
      <h4 className="font-bold text-gray-900 text-sm">New Message</h4>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
      <X className="w-4 h-4" />
    </button>
  </div>
);

export const StudentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { bookings, properties, payments, messages, savedProperties, toggleSavedProperty, sendMessage, payPayment, referralCampaigns, users, requestWithdrawal, markMessagesAsRead } = useData();
  const { user, updateProfile } = useAuth();

  // --- Sub-States ---
  const [selectedChatUser, setSelectedChatUser] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  
  // Toast State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [lastMsgId, setLastMsgId] = useState<string | null>(null);

  // Withdrawal Form State
  const [withdrawForm, setWithdrawForm] = useState({
      bankName: '',
      accountNumber: '',
      accountHolderName: '',
      branchCode: '',
      idNumber: ''
  });

  // Calculate Unread Messages for Badge
  const unreadTotal = messages.filter(m => m.receiverId === user?.id && !m.isRead).length;

  const sidebarItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'bookings', label: 'My Bookings', icon: Calendar },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'messages', label: 'Messages', icon: MessageSquare, count: unreadTotal }, // Pass unread count here
    { id: 'referrals', label: 'Referrals & Rewards', icon: ShoppingCart },
    { id: 'saved', label: 'Saved Listings', icon: Bookmark },
    { id: 'profile', label: 'Profile', icon: UserIcon },
  ];

  // --- Listen for new messages to show Toast ---
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      // Check if it's a new message, directed to me, and unread
      if (lastMsg.id !== lastMsgId && lastMsg.receiverId === user?.id && !lastMsg.isRead) {
        // Find sender name
        const sender = users.find(u => u.id === lastMsg.senderId);
        const senderName = sender?.name || 'Unknown User';
        
        setToastMessage(`You have a new message from ${senderName}`);
        setShowToast(true);
        setLastMsgId(lastMsg.id);

        // Auto-hide toast after 5s
        setTimeout(() => setShowToast(false), 5000);
      } else if (lastMsg.id !== lastMsgId) {
         setLastMsgId(lastMsg.id); // Sync ID if sent by me or read
      }
    }
  }, [messages, user, lastMsgId, users]);

  // --- Helpers ---
  const upcomingPayments = payments.filter(p => p.status === 'PENDING');
  const savedPropsList = properties.filter(p => savedProperties.includes(p.id));
  const activeCampaign = referralCampaigns.find(c => c.isActive);
  
  const currentUser = users.find(u => u.id === user?.id);
  const referralCode = currentUser?.referralCode || 'LOADING...';
  const referralBalance = currentUser?.referralBalance || 0;
  
  const REWARD_AMOUNT = activeCampaign?.rewardAmount || 50;
  const THRESHOLD = activeCampaign?.thresholdAmount || 300;
  const progressPercent = Math.min((referralBalance / THRESHOLD) * 100, 100);

  // Group messages
  const conversations = Array.from(new Set([...messages.map(m => m.senderId), ...messages.map(m => m.receiverId)]))
    .filter(id => id !== user?.id)
    .map(id => {
       const lastMsg = messages.filter(m => m.senderId === id || m.receiverId === id).sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
       return {
          userId: id,
          name: lastMsg?.senderName || users.find(u => u.id === id)?.name || `User ${id.substring(0,4)}`,
          lastMessage: lastMsg?.content,
          timestamp: lastMsg?.createdAt,
          unread: !lastMsg?.isRead && lastMsg?.receiverId === user?.id
       };
    });

  const activeConversation = selectedChatUser ? messages.filter(m => (m.senderId === user?.id && m.receiverId === selectedChatUser) || (m.senderId === selectedChatUser && m.receiverId === user?.id)).sort((a,b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) : [];

  const handleChatSelect = (userId: string) => {
    setSelectedChatUser(userId);
    // Mark as read when selecting the conversation
    markMessagesAsRead(userId);
  };

  const handleSendMessage = async () => {
     if (!selectedChatUser || !chatInput.trim()) return;
     await sendMessage(selectedChatUser, chatInput);
     setChatInput('');
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
     e.preventDefault();
     const form = e.target as HTMLFormElement;
     const formData = new FormData(form);
     await updateProfile({
        name: formData.get('name') as string,
        phoneNumber: formData.get('phone') as string,
        institution: formData.get('institution') as string,
     });
     alert('Profile updated successfully!');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleSubmitWithdrawal = async (e: React.FormEvent) => {
      e.preventDefault();
      if (referralBalance < THRESHOLD) return;
      
      const success = await requestWithdrawal({
          userId: user!.id,
          amount: referralBalance, // Withdraw full amount for now
          email: user!.email,
          bankName: withdrawForm.bankName,
          accountNumber: withdrawForm.accountNumber,
          accountHolderName: withdrawForm.accountHolderName,
          branchCode: withdrawForm.branchCode,
          idNumber: withdrawForm.idNumber
      });

      if (success) {
          setWithdrawSuccess(true);
          setShowWithdrawModal(false);
          setWithdrawForm({
              bankName: '',
              accountNumber: '',
              accountHolderName: '',
              branchCode: '',
              idNumber: ''
          });
      }
  };

  return (
    <DashboardLayout title="Student Dashboard" sidebarItems={sidebarItems} activeTab={activeTab} onTabChange={setActiveTab}>
      
      {/* Global Toast */}
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}

      {/* --- OVERVIEW TAB --- */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user?.name.split(' ')[0]}! 👋</h2>
              <p className="text-gray-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-500" />
                You have <span className="font-bold text-gray-900">{upcomingPayments.length} upcoming payments</span> due.
              </p>
            </div>
            <div className="flex gap-3">
               <Button onClick={() => setActiveTab('bookings')}>View Bookings</Button>
               <Button variant="outline" onClick={() => window.location.href = '#/browse?status=available'}>Browse Homes</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
             <div className="lg:col-span-2 space-y-8">
                {/* Upcoming Payments Widget */}
                <section>
                   <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">Upcoming Payments</h3>
                      <button onClick={() => setActiveTab('payments')} className="text-sm text-[#2f5c96] font-medium hover:underline">View History</button>
                   </div>
                   {upcomingPayments.length > 0 ? (
                      <div className="grid gap-4">
                         {upcomingPayments.map(payment => (
                            <Card key={payment.id} className="p-5 flex flex-col sm:flex-row items-center justify-between gap-4 border-l-4 border-l-orange-400">
                               <div className="flex items-center gap-4 w-full sm:w-auto">
                                  <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-orange-500">
                                     <CreditCard className="w-6 h-6" />
                                  </div>
                                  <div>
                                     <h4 className="font-bold text-gray-900">{payment.title}</h4>
                                     <p className="text-sm text-gray-500">Due {new Date(payment.dueDate).toLocaleDateString()}</p>
                                  </div>
                                </div>
                               <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                  <span className="font-bold text-lg text-gray-900">R{payment.amount}</span>
                                  <Button size="sm" variant="primary" onClick={() => setPaymentModalOpen(payment.id)}>Pay Now</Button>
                               </div>
                            </Card>
                         ))}
                      </div>
                   ) : (
                      <Card className="p-8 text-center bg-gray-50 border-dashed">
                         <p className="text-gray-500">No pending payments. You're all caught up!</p>
                      </Card>
                   )}
                </section>
             </div>

             <div className="space-y-8">
                {/* Referal Teaser Widget */}
                <Card className="p-6 bg-gradient-to-br from-[#2f5c96] to-[#4a7abd] text-white">
                   <div className="flex items-start justify-between mb-4">
                      <Gift className="w-8 h-8 text-white/80" />
                      <Badge className="bg-white/20 text-white border-0">Earn R{REWARD_AMOUNT}</Badge>
                   </div>
                   <h3 className="text-lg font-bold mb-2">Get Cash Rewards</h3>
                   <p className="text-sm text-blue-100 mb-4">Refer a friend and earn R{REWARD_AMOUNT} cash directly to your bank account.</p>
                   <Button variant="white" size="sm" className="w-full" onClick={() => setActiveTab('referrals')}>Get My Code</Button>
                </Card>

                <Card className="p-5">
                   <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-gray-900">Messages</h3>
                      {unreadTotal > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadTotal} New</span>}
                      <button onClick={() => setActiveTab('messages')} className="p-1 hover:bg-gray-100 rounded">
                         <ChevronRight className="w-4 h-4 text-gray-500" />
                      </button>
                   </div>
                   <div className="space-y-4">
                      {conversations.length > 0 ? conversations.slice(0, 3).map(conv => (
                         <div key={conv.userId} onClick={() => { setActiveTab('messages'); handleChatSelect(conv.userId); }} className="flex gap-3 items-start cursor-pointer hover:bg-gray-50 p-2 rounded-lg -mx-2">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center text-gray-500 text-xs font-bold relative">
                               {conv.name.charAt(0)}
                               {conv.unread && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>}
                            </div>
                            <div className="flex-1 min-w-0">
                               <div className="flex justify-between items-baseline mb-1">
                                  <h4 className={`text-sm ${conv.unread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} truncate`}>{conv.name}</h4>
                                  <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(conv.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                               </div>
                               <p className={`text-xs truncate ${conv.unread ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>{conv.lastMessage}</p>
                            </div>
                         </div>
                      )) : <p className="text-sm text-gray-500">No recent messages.</p>}
                   </div>
                </Card>
             </div>
          </div>
        </div>
      )}

      {/* ... (Rest of the Tabs: Referrals, Withdrawals, Payments, Bookings - unchanged) ... */}
      
      {activeTab === 'referrals' && (
         <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center mb-8">
               <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Refer & Earn Cash</h2>
               <p className="text-gray-500 text-lg">Invite friends to book their accommodation and earn R{REWARD_AMOUNT} per referral.</p>
            </div>

            {/* Progress & Balance Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
               <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6">
                     <div className="text-center md:text-left">
                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Current Balance</p>
                        <h3 className="text-5xl font-extrabold text-[#2f5c96]">R{referralBalance}</h3>
                     </div>
                     <div className="text-center md:text-right">
                        <p className="text-sm font-medium text-gray-500 mb-1">Threshold: R{THRESHOLD}</p>
                        <Button 
                           onClick={() => setShowWithdrawModal(true)} 
                           disabled={referralBalance < THRESHOLD} 
                           className={`px-8 py-3 text-lg ${referralBalance >= THRESHOLD ? 'animate-pulse shadow-lg shadow-blue-200' : ''}`}
                        >
                           {referralBalance >= THRESHOLD ? 'Withdraw Funds' : `R${THRESHOLD - referralBalance} more to withdraw`}
                        </Button>
                     </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative pt-1">
                     <div className="flex mb-2 items-center justify-between">
                        <div>
                           <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                              Goal Progress
                           </span>
                        </div>
                        <div className="text-right">
                           <span className="text-xs font-semibold inline-block text-blue-600">
                              {Math.round(progressPercent)}%
                           </span>
                        </div>
                     </div>
                     <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-blue-100">
                        <div style={{ width: `${progressPercent}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#2f5c96] transition-all duration-1000 ease-out"></div>
                     </div>
                     <p className="text-xs text-gray-400 text-center">
                        *You can request a cash withdrawal to your bank account once you reach R{THRESHOLD}.
                     </p>
                  </div>
               </div>
               
               {withdrawSuccess && (
                  <div className="bg-green-50 p-6 border-t border-green-100 text-center">
                     <Check className="w-12 h-12 text-green-500 mx-auto mb-2" />
                     <h4 className="text-xl font-bold text-green-800 mb-1">Withdrawal Requested!</h4>
                     <p className="text-green-700">Your request has been sent to the admin team for processing.</p>
                     <Button variant="ghost" size="sm" onClick={() => setWithdrawSuccess(false)} className="mt-4">Dismiss</Button>
                  </div>
               )}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
               {/* Code Card */}
               <Card className="p-8 text-center flex flex-col justify-center border-t-4 border-t-[#c19cca]">
                  <h4 className="text-lg font-bold text-gray-700 mb-4 uppercase tracking-wide">Your Unique Code</h4>
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-6 mb-6">
                     <span className="text-3xl font-mono font-bold text-gray-800 tracking-widest">{referralCode}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                     Share this code with friends. When they book using your code, you earn R{REWARD_AMOUNT} instantly.
                  </p>
                  <Button onClick={copyReferralCode} className="w-full py-3" variant={copiedCode ? 'secondary' : 'primary'}>
                     {copiedCode ? <><Check className="w-5 h-5 mr-2" /> Copied!</> : <><Copy className="w-5 h-5 mr-2" /> Copy Code</>}
                  </Button>
               </Card>

               {/* Stats Card */}
               <Card className="p-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-6">Earnings History</h4>
                  <div className="space-y-4">
                     <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                           <UserIcon className="w-5 h-5 text-gray-400" />
                           <span className="font-medium text-gray-700">Friends Referred</span>
                        </div>
                        <span className="text-xl font-bold text-gray-900">
                           {Math.floor((currentUser?.lifetimeEarnings || 0) / REWARD_AMOUNT)}
                        </span>
                     </div>
                     <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                           <Gift className="w-5 h-5 text-green-600" />
                           <span className="font-medium text-gray-700">Total Earned</span>
                        </div>
                        <span className="text-xl font-bold text-green-600">R{currentUser?.lifetimeEarnings || 0}</span>
                     </div>
                  </div>
               </Card>
            </div>
         </div>
      )}

      {showWithdrawModal && (
         <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
            <Card className="max-w-lg w-full p-0 overflow-hidden animate-scaleIn">
               <div className="bg-[#2f5c96] px-6 py-4 flex justify-between items-center text-white">
                  <h3 className="font-bold text-lg flex items-center gap-2"><Banknote className="w-5 h-5" /> Request Withdrawal</h3>
                  <button onClick={() => setShowWithdrawModal(false)} className="hover:bg-white/10 rounded-full p-1"><span className="sr-only">Close</span>× </button>
               </div>
               <div className="p-6">
                  <div className="mb-6 bg-blue-50 border border-blue-100 p-4 rounded-lg">
                     <p className="text-sm text-blue-800">You are requesting to withdraw <strong>R{referralBalance}</strong>.</p>
                  </div>
                  <form onSubmit={handleSubmitWithdrawal} className="space-y-4">
                     <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name</label>
                        <input 
                           required 
                           type="text" 
                           className="w-full border p-2 rounded" 
                           value={withdrawForm.accountHolderName}
                           onChange={e => setWithdrawForm({...withdrawForm, accountHolderName: e.target.value})}
                           placeholder="As per bank account"
                        />
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">ID Number</label>
                           <input 
                              required 
                              type="text" 
                              className="w-full border p-2 rounded" 
                              value={withdrawForm.idNumber}
                              onChange={e => setWithdrawForm({...withdrawForm, idNumber: e.target.value})}
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                           <input 
                              type="email" 
                              className="w-full border p-2 rounded bg-gray-100 text-gray-500" 
                              value={user?.email}
                              readOnly
                           />
                        </div>
                     </div>
                     <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bank Name</label>
                        <input 
                           required 
                           type="text" 
                           className="w-full border p-2 rounded" 
                           value={withdrawForm.bankName}
                           onChange={e => setWithdrawForm({...withdrawForm, bankName: e.target.value})}
                           placeholder="e.g. FNB, Capitec"
                        />
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Number</label>
                           <input 
                              required 
                              type="text" 
                              className="w-full border p-2 rounded" 
                              value={withdrawForm.accountNumber}
                              onChange={e => setWithdrawForm({...withdrawForm, accountNumber: e.target.value})}
                           />
                        </div>
                        <div>
                           <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Branch Code</label>
                           <input 
                              required 
                              type="text" 
                              className="w-full border p-2 rounded" 
                              value={withdrawForm.branchCode}
                              onChange={e => setWithdrawForm({...withdrawForm, branchCode: e.target.value})}
                           />
                        </div>
                     </div>
                     
                     <div className="pt-4 flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowWithdrawModal(false)}>Cancel</Button>
                        <Button type="submit">Submit Request</Button>
                     </div>
                  </form>
               </div>
            </Card>
         </div>
      )}

      {activeTab === 'payments' && (
         <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Payments & History</h2>
            <Card className="overflow-hidden">
               <div className="divide-y divide-gray-100">
                  {payments.length > 0 ? payments.map(p => (
                     <div key={p.id} className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                           <div className={`w-10 h-10 rounded-full flex items-center justify-center ${p.status === 'COMPLETED' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                              <CreditCard className="w-5 h-5" />
                           </div>
                           <div>
                              <p className="font-bold text-gray-900">{p.title}</p>
                              <p className="text-xs text-gray-500">{p.status === 'COMPLETED' ? `Paid on ${new Date(p.paidAt!).toLocaleDateString()}` : `Due ${new Date(p.dueDate).toLocaleDateString()}`}</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                           <span className="font-bold text-gray-900">R{p.amount}</span>
                           {p.status === 'PENDING' ? (
                              <Button size="sm" onClick={() => setPaymentModalOpen(p.id)}>Pay Now</Button>
                           ) : (
                              <Badge color="green">Paid</Badge>
                           )}
                        </div>
                     </div>
                  )) : (
                     <div className="p-8 text-center text-gray-500 bg-white">No payment history found.</div>
                  )}
               </div>
            </Card>
         </div>
      )}

      {activeTab === 'bookings' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">My Bookings</h2>
          {bookings.length > 0 ? (
            <div className="grid gap-4">
              {bookings.map(booking => {
                const prop = properties.find(p => p.id === booking.propertyId);
                return (
                  <Card key={booking.id} className="p-4 flex flex-col md:flex-row gap-4 items-center">
                     <div className="w-full md:w-32 h-24 bg-gray-200 rounded-lg overflow-hidden shrink-0">
                       {prop?.image ? (
                         <img src={prop.image} alt="" className="w-full h-full object-cover"/>
                       ) : (
                         <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400"><Home className="w-6 h-6"/></div>
                       )}
                     </div>
                     <div className="flex-1 w-full md:w-auto">
                        <h3 className="font-bold text-lg">{prop?.title || 'Unknown Property'}</h3>
                        <p className="text-gray-500 text-sm">Booking Date: {new Date(booking.date).toLocaleDateString()}</p>
                        <div className="mt-2">
                          <Badge color={booking.status === 'CONFIRMED' ? 'green' : (booking.status === 'COMPLETED' ? 'gray' : 'yellow')}>{booking.status}</Badge>
                        </div>
                     </div>
                     <Button variant="outline" onClick={() => window.location.href=`#/property/${booking.propertyId}`} className="w-full md:w-auto">View Property</Button>
                  </Card>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">You haven't booked any accommodation yet.</p>
              <Button onClick={() => window.location.href='#/browse?status=available'}>Browse Homes</Button>
            </div>
          )}
        </div>
      )}

      {/* --- MESSAGES TAB --- */}
      {activeTab === 'messages' && (
        <div className="h-[calc(100vh-200px)] flex flex-col">
           <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Messages</h2>
           </div>
           
           <Card className="flex-1 flex overflow-hidden">
              {/* Inbox List */}
              <div className="w-full md:w-1/3 border-r border-gray-100 flex flex-col">
                 <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                       <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                       <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2f5c96]" />
                    </div>
                 </div>
                 <div className="overflow-y-auto flex-1">
                    {conversations.length > 0 ? conversations.map(conv => (
                       <div key={conv.userId} onClick={() => handleChatSelect(conv.userId)} className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${selectedChatUser === conv.userId ? 'bg-blue-50/50' : ''}`}>
                          <div className="flex justify-between items-start mb-1">
                             <span className={`text-sm ${conv.unread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{conv.name}</span>
                             <span className="text-xs text-gray-400">{new Date(conv.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                          </div>
                          <p className={`text-xs truncate ${conv.unread ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>{conv.lastMessage}</p>
                       </div>
                    )) : <div className="p-8 text-center text-gray-500 text-sm">No messages yet.</div>}
                 </div>
              </div>

              {/* Chat Area */}
              <div className="hidden md:flex flex-1 flex-col bg-slate-50/30">
                 {selectedChatUser ? (
                   <>
                     <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center">
                        <h3 className="font-bold text-gray-900">{conversations.find(c => c.userId === selectedChatUser)?.name || 'Chat'}</h3>
                     </div>
                     <div className="flex-1 p-6 overflow-y-auto space-y-4">
                        {activeConversation.map(msg => (
                          <div key={msg.id} className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}>
                             <div className={`p-3 rounded-lg shadow-sm max-w-sm ${msg.senderId === user?.id ? 'bg-[#2f5c96] text-white rounded-br-none' : 'bg-white border border-gray-100 rounded-bl-none'}`}>
                                <p className="text-sm">{msg.content}</p>
                                <span className={`text-[10px] block text-right mt-1 ${msg.senderId === user?.id ? 'text-blue-100' : 'text-gray-400'}`}>
                                  {new Date(msg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                             </div>
                          </div>
                        ))}
                     </div>
                     <div className="p-4 bg-white border-t border-gray-100">
                        <div className="flex gap-2">
                           <input 
                              type="text" 
                              placeholder="Type a message..." 
                              className="flex-1 px-4 py-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#2f5c96]" 
                              value={chatInput}
                              onChange={e => setChatInput(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                           />
                           <Button onClick={handleSendMessage}><Send className="w-4 h-4" /></Button>
                        </div>
                     </div>
                   </>
                 ) : (
                   <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                      <MessageSquare className="w-12 h-12 mb-2 opacity-20" />
                      <p>Select a conversation to start chatting</p>
                   </div>
                 )}
              </div>
           </Card>
        </div>
      )}

      {/* --- SAVED LISTINGS TAB --- */}
      {activeTab === 'saved' && (
        <div className="space-y-6">
           <h2 className="text-2xl font-bold text-gray-900">Saved Listings</h2>
           {savedPropsList.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 {savedPropsList.map(p => (
                    <Card key={p.id} className="overflow-hidden flex flex-col group">
                       <div className="relative h-48 overflow-hidden">
                          <img src={p.image} className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500" />
                          <div className="absolute top-2 right-2">
                             <button onClick={() => toggleSavedProperty(p.id)} className="p-2 bg-white rounded-full shadow-sm hover:bg-red-50 text-red-500 transition-colors">
                                <Trash2 className="w-4 h-4" />
                             </button>
                          </div>
                       </div>
                       <div className="p-4 flex-1 flex flex-col">
                          <div className="flex-1">
                             <h3 className="font-bold text-gray-900 mb-1">{p.title}</h3>
                             <p className="text-sm text-gray-500 mb-2">{p.location}</p>
                             <p className="text-[#2f5c96] font-bold">R{p.price}/mo</p>
                          </div>
                          <Button className="w-full mt-4" variant="outline" onClick={() => window.location.href=`#/property/${p.id}`}>View Details</Button>
                       </div>
                    </Card>
                 ))}
              </div>
           ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                 <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                 <p className="text-gray-500 mb-4">No saved properties found.</p>
                 <Button onClick={() => window.location.href='#/browse?status=available'}>Browse Homes</Button>
              </div>
           )}
        </div>
      )}

      {/* --- PROFILE TAB --- */}
      {activeTab === 'profile' && (
        <div className="max-w-2xl">
           <h2 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h2>
           <Card className="p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 text-center sm:text-left">
                 <img src={user?.avatar} alt={user?.name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg bg-gray-200" />
                 <div>
                    <h3 className="text-xl font-bold text-gray-900">{user?.name}</h3>
                    <p className="text-gray-500">{user?.email}</p>
                    <Badge className="mt-2 inline-block">{user?.role}</Badge>
                 </div>
              </div>
              
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                       <input name="name" defaultValue={user?.name} className="w-full p-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-[#2f5c96]" />
                    </div>
                    <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                       <input name="phone" defaultValue={user?.phoneNumber} className="w-full p-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-[#2f5c96]" />
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">University / Institution</label>
                       <input name="institution" defaultValue={user?.institution} className="w-full p-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-[#2f5c96]" />
                    </div>
                 </div>
                 
                 <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <Button type="submit">Save Changes</Button>
                 </div>
              </form>
           </Card>
        </div>
      )}
      
      {/* --- PAYMENT MODAL --- */}
      {paymentModalOpen && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <Card className="max-w-md w-full p-8">
               <div className="space-y-3">
                  <Button className="w-full py-3" onClick={() => { payPayment(paymentModalOpen); setPaymentModalOpen(null); }}>
                     Pay Now
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => setPaymentModalOpen(null)}>
                     Cancel
                  </Button>
               </div>
            </Card>
         </div>
      )}

    </DashboardLayout>
  );
};